/**
 * LatestReleaseTebexPicker — single-package picker modal for the admin's
 * "Latest Release" featured card. Opens, lists every Tebex storefront package,
 * lets the admin click ONE to link it. On select, the parent receives the full
 * package object so it can optionally auto-fill title/description/image.
 */
import { useEffect, useMemo, useState } from "react";
import { X, Loader2, Search, AlertCircle, Check, ShoppingBag } from "lucide-react";
import { fetchTebexPackages } from "@/api";

export default function LatestReleaseTebexPicker({ open, onClose, currentId, onPick, testIdPrefix = "latestrelease-tebex-picker", title = "Link a Tebex Product" }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [packages, setPackages] = useState([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    setSearch("");
    let cancelled = false;
    setLoading(true);
    fetchTebexPackages()
      .then((d) => {
        if (cancelled) return;
        setPackages(d.packages || []);
        if (!(d.packages || []).length) {
          setError("Tebex returned 0 packages — make sure your store has published packages.");
        }
      })
      .catch((e) => {
        if (!cancelled) setError(e.response?.data?.detail || e.message || "Failed to load Tebex packages");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return packages;
    return packages.filter(
      (p) => (p.name || "").toLowerCase().includes(q) || (p.category || "").toLowerCase().includes(q)
    );
  }, [packages, search]);

  if (!open) return null;

  return (
    <div
      data-testid={testIdPrefix}
      className="fixed inset-0 z-[140] bg-black/80 backdrop-blur-md flex items-center justify-center p-6"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-[#0a0a0a] border border-[var(--accent)] w-full max-w-2xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-[var(--accent)]/40 px-5 py-4">
          <div className="flex items-center gap-3">
            <ShoppingBag size={16} className="text-[var(--accent)]" />
            <div>
              <div className="bracket text-[var(--accent)]">[ TEBEX_PACKAGES ]</div>
              <h3 className="font-display text-base font-bold uppercase tracking-tight">
                {title}
              </h3>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-[var(--muted)] hover:text-white transition"
            data-testid={`${testIdPrefix}-close`}
          >
            <X size={18} />
          </button>
        </div>

        {/* Search */}
        <div className="px-5 py-3 border-b border-[var(--line-strong)]">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search packages..."
              className="w-full bg-black border border-[var(--line-strong)] focus:border-[var(--accent)] outline-none pl-9 pr-3 py-2 text-sm text-white font-mono"
              data-testid={`${testIdPrefix}-search`}
            />
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto px-2 py-2">
          {loading && (
            <div className="flex items-center justify-center py-12 text-[var(--muted)] font-mono text-xs">
              <Loader2 size={14} className="animate-spin mr-2" /> Loading packages from Tebex...
            </div>
          )}
          {!loading && error && (
            <div className="flex items-start gap-2 p-4 m-3 border border-red-500/40 bg-red-500/10 text-red-300 font-mono text-xs">
              <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
          {!loading && !error && filtered.map((p) => {
            const isCurrent = String(p.id) === String(currentId || "");
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => {
                  onPick(p);
                  onClose();
                }}
                className={`w-full flex items-center gap-3 px-3 py-3 border my-1 text-left transition ${
                  isCurrent
                    ? "border-[var(--accent)] bg-[var(--accent)]/10"
                    : "border-[var(--line-strong)] hover:border-[var(--accent)]/60 hover:bg-white/5"
                }`}
                data-testid={`${testIdPrefix}-pick-${p.id}`}
              >
                {p.image ? (
                  <img src={p.image} alt="" className="w-12 h-12 object-cover border border-[var(--line-strong)] flex-shrink-0" />
                ) : (
                  <div className="w-12 h-12 border border-[var(--line-strong)] bg-black flex items-center justify-center flex-shrink-0">
                    <ShoppingBag size={14} className="text-[var(--muted)]" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-sm text-white truncate">{p.name || "(no name)"}</div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] mt-0.5 flex items-center gap-2">
                    <span>ID: {p.id}</span>
                    {p.price && <span className="text-[var(--accent)]">{p.price}</span>}
                    {p.category && <span>// {p.category}</span>}
                  </div>
                </div>
                {isCurrent && <Check size={16} className="text-[var(--accent)] flex-shrink-0" />}
              </button>
            );
          })}
          {!loading && !error && filtered.length === 0 && (
            <div className="text-center py-12 text-[var(--muted)] font-mono text-xs">
              No packages match your search.
            </div>
          )}
        </div>

        {/* Footer hint */}
        <div className="border-t border-[var(--line-strong)] px-5 py-3 font-mono text-[10px] text-[var(--muted)] uppercase tracking-widest">
          // Click a package to link it. Title / description / image will be auto-filled (you can still edit them).
        </div>
      </div>
    </div>
  );
}
